import { makeInitialsAvatar } from "./avatars.ts";
import {
  DEMO_COMPANY_HOLDINGS_AG,
  DEMO_COMPANY_LABS_SP,
  DEMO_COMPANY_STUDIO_SRO,
  DEMO_COMPANY_TRADE_LTD,
  DEMO_COMPANY_VENTURES_GMBH,
  type DemoCompanyId,
} from "./identifiers.ts";

export type DemoCompany = {
  id: DemoCompanyId;
  name: string;
  shortName: string;
  registrationNumber: string;
  vatNumber: string;
  jurisdiction: "AT" | "CZ" | "PL" | "DE" | "GB";
  defaultCurrency: "EUR" | "CZK" | "PLN" | "GBP";
  street: string;
  city: string;
  zip: string;
  founded: string;
  description: string;
  // Anna's role within the company — drives sidebar + entitlements UI labels.
  annaRole: "CFO" | "Admin" | "Employee";
  imgSrc: string;
};

const make = (c: Omit<DemoCompany, "imgSrc">): DemoCompany => ({
  ...c,
  imgSrc: makeInitialsAvatar(c.shortName),
});

export const DEMO_COMPANIES: DemoCompany[] = [
  make({
    id: DEMO_COMPANY_HOLDINGS_AG,
    name: "Contoso Holdings AG",
    shortName: "Contoso Holdings",
    registrationNumber: "FN 487120 b",
    vatNumber: "ATU74829513",
    jurisdiction: "AT",
    defaultCurrency: "EUR",
    street: "Mariahilfer Straße 88",
    city: "Vienna",
    zip: "1070",
    founded: "2016-04-12",
    description:
      "Group holding company. Operates corporate treasury, investor relations and central FX.",
    annaRole: "CFO",
  }),
  make({
    id: DEMO_COMPANY_STUDIO_SRO,
    name: "Contoso Studio s.r.o.",
    shortName: "Contoso Studio",
    registrationNumber: "C 198432",
    vatNumber: "CZ09183746",
    jurisdiction: "CZ",
    defaultCurrency: "CZK",
    street: "Karolinská 654/2",
    city: "Prague",
    zip: "186 00",
    founded: "2018-09-04",
    description:
      "Design + product studio. Builds custom software for Contoso Group clients.",
    annaRole: "Admin",
  }),
  make({
    id: DEMO_COMPANY_LABS_SP,
    name: "Contoso Labs Sp. z o.o.",
    shortName: "Contoso Labs",
    registrationNumber: "KRS 0000812047",
    vatNumber: "PL5252839472",
    jurisdiction: "PL",
    defaultCurrency: "PLN",
    street: "ul. Złota 59",
    city: "Warsaw",
    zip: "00-120",
    founded: "2020-02-18",
    description:
      "Engineering hub. Data + ML R&D, serves the Group's internal tooling.",
    annaRole: "Admin",
  }),
  make({
    id: DEMO_COMPANY_VENTURES_GMBH,
    name: "Contoso Ventures GmbH",
    shortName: "Contoso Ventures",
    registrationNumber: "HRB 248192",
    vatNumber: "DE329104872",
    jurisdiction: "DE",
    defaultCurrency: "EUR",
    street: "Friedrichstraße 68",
    city: "Berlin",
    zip: "10117",
    founded: "2021-06-30",
    description: "Early-stage portfolio company. Direct investments into B2B SaaS.",
    annaRole: "Admin",
  }),
  make({
    id: DEMO_COMPANY_TRADE_LTD,
    name: "Contoso Trade Ltd.",
    shortName: "Contoso Trade",
    registrationNumber: "13298471",
    vatNumber: "GB392184710",
    jurisdiction: "GB",
    defaultCurrency: "GBP",
    street: "21 Charterhouse Square",
    city: "London",
    zip: "EC1M 6HJ",
    founded: "2022-11-08",
    description:
      "UK trading + licensing entity. Cross-border invoicing and royalty collection.",
    annaRole: "Employee",
  }),
];

export const DEMO_COMPANY_BY_ID = Object.fromEntries(
  DEMO_COMPANIES.map((c) => [c.id, c]),
) as Record<DemoCompanyId, DemoCompany>;
